import { LISTING_PAGE_MARKER, LISTING_PAGE_FLAG } from '../constants'

// ---------------------------------------------------------------------------
// Coveo Helpers
// ---------------------------------------------------------------------------
/**
 * Parameters that the Coveo commerce urlManager serializes/deserializes.
 */
const coveoBasicParams = new Set(['q', 'sortCriteria', 'page', 'perPage'])
const coveoFacetPrefixes = ['f-', 'fExcluded-', 'cf-', 'nf-', 'nfExcluded-', 'df-', 'dfExcluded-', 'mnf-', 'mnfExcluded-', 'lf-']

export const isCoveoParam = (key) => {
  if (coveoBasicParams.has(key)) return true
  return coveoFacetPrefixes.some((prefix) => key.startsWith(prefix))
}

/**
 * Parameters that the classic (@coveo/headless) content urlManager serializes for
 * automatic facets — e.g. "af-industry=Retail,Healthcare".
 */
const contentBasicParams = new Set(['q', 'sortCriteria', 'firstResult', 'numberOfResults'])
const AF_PREFIX = 'af-'
const contentFacetPrefixes = [AF_PREFIX]
export const isContentFacetParam = (key) => {
  if (contentBasicParams.has(key)) return true
  return contentFacetPrefixes.some((prefix) => key.startsWith(prefix))
}

export const CONTENT_FACET_LABELS = {
  author: 'Author',
  topics: 'Topics',
  industry: 'Industry',
  contenttype: 'Content type',
  keywords: 'Keywords',
  allKeywords_ss: 'Keywords',
}

export const CONTENT_CHECKBOX_FACETS = [
  'author',
  'topics',
  'industry',
  'contenttype',
  'keywords',
]

export const CONTENT_RESULT_FIELDS = [
  'description',
  'imageUrl',
  'createdDate',
  'contentType',
  'author',
  'industry',
  'topics',
  'date'
]
const isTrue = (value) => {
  return value === true || value === 'true';
}

const toSingleString = (value) => {
  if (Array.isArray(value)) {
    const first = value.find((v) => v != null && `${v}`.trim() !== '')
    return first != null ? `${first}` : ''
  }
  return value != null ? `${value}` : ''
}

/**
 * Maps a Coveo Commerce Product to the product shape expected by ListItem.js
 * and the rest of the existing product-list components.
 */
export const mapResultToProduct = (product) => {
  const af = product?.additionalFields || {}

  const materialId = (() => {
    try {
      return decodeURIComponent(af?.inh_sku || '')
    } catch {
      return af?.inh_sku || ''
    }
  })()
  const materialQualifier = toSingleString(af?.inh_material_qualifier) || '';
  const description = toSingleString(af?.inc_short_desc || af?.inc_std_desc) || '';
  const image = toSingleString(af?.inp_main_product_image) || toSingleString(af?.inp_mfr_image_url) || '';
  const manufacturerName = toSingleString(af?.ini_mfr_name) || '';
  const manufacturerPartNumber = toSingleString(af?.inp_mfr_part_num) || '';
  const availability = af?.ini_product_avail_sales_org || 'AVAILABLE';
  const listPrice = af?.ins_list_price_sales_org;
  const webPrice = af?.ins_insight_price_sales_org;
  const bullet1 = af?.inp_web_bullet_line1 || '';
  const bullet2 = af?.inp_web_bullet_line2 || '';
  const bullet3 = af?.inp_web_bullet_line3 || '';
  const bullet4 = af?.inp_web_bullet_line4 || '';
  const bullet5 = af?.inp_web_bullet_line5 || '';
  const leasingpromo = af?.inp_leasing_promo || null;

  const listPriceInclVat = af?.ins_list_price_incl_tax_sales_org;
  const webPriceInclVat = af?.ins_insight_price_incl_tax_sales_org;
  const priceLabel = af?.inp_price_label || '';
  const callForPrice = isTrue(af?.inp_call_for_price);
  const displayDiscount = isTrue(af?.inp_display_discount);
  const availabilityMessage = af?.inp_availability_message || '';
  const regularStock = Number(af?.inp_regular_stock ?? 0);
  const unlimited = isTrue(af?.inp_unlimited);
  const proratable = isTrue(af?.inp_proratable);
  const category = toSingleString(af?.inp_category) || '';

  return {
    materialId: materialId,
    description:            description,
    manufacturerName:       manufacturerName,
    manufacturerPartNumber: manufacturerPartNumber,
    materialQualifier:      materialQualifier,

    insightPrice: webPrice,
    price: {
      webPrice,
      listPrice,
      indexPrice:       listPrice,
      displayDiscount:  displayDiscount,
      priceLabel:       priceLabel,
      listPriceInclVat: listPriceInclVat,
      webPriceInclVat:  webPriceInclVat,
      callForPrice:     callForPrice,
    },
    callForPrice:       callForPrice,

    availability,
    availabilityMessage: availabilityMessage,
    regularStock:        regularStock,
    unlimited:           unlimited,
    inStock:             product?.ec_in_stock ?? null,

    bullet1: bullet1,
    bullet2: bullet2,
    bullet3: bullet3,
    bullet4: bullet4,
    bullet5: bullet5,

    proratable:    proratable,
    leasingPromo:  leasingpromo || null,

    image,
    imageUrl: image,
    category: category,

    _coveoProduct: product,
  }
}

export const mapClassicResultToContentDoc = (result) => {
  const raw = result?.raw || {}
  const toArray = (value) => {
    if (Array.isArray(value)) return value
    if (value == null || value === '') return []
    return String(value)
      .split('|')
      .map((item) => item.trim())
      .filter(Boolean)
  }

  return {
    title: result?.title || raw.title || raw.pagetitle || '',
    displayDate: raw.displaydate || raw.date || '',
    pagePath:
      raw.pagepath ||
      raw.clickableuri ||
      raw.clickuri ||
      raw.uri ||
      result?.clickUri ||
      result?.uri ||
      '#',
    contentType: raw.contenttype || raw.filetype || raw.objecttype || 'Article',
    description: result?.excerpt || raw.description || raw.meta_description || '',
    imageUrl: raw.imageurl || raw.thumbnail || raw.image || '',
    topics: toArray(raw.topics || raw.topic || raw.tags),
  }
}

/*
* Resolves label from a Coveo breadcrumb value entry.
* @param {string|object} breadcrumbValue
* @returns {string}
*/
const resolveBreadcrumbLabel = (breadcrumbValue) => {
  if (breadcrumbValue === null || breadcrumbValue === undefined) return ''

  // Shape 1 — flat string (classic headless automatic facets)
  if (typeof breadcrumbValue === 'string') return breadcrumbValue

  if (typeof breadcrumbValue === 'object') {
    // Shape 2 — numeric / date range: derive label from bounds
    if (breadcrumbValue.start !== undefined && breadcrumbValue.end !== undefined) {
      return `${breadcrumbValue.start} - ${breadcrumbValue.end}`
    }
    // Shape 3 — hierarchical: derive label from path array
    if (breadcrumbValue.path !== undefined && breadcrumbValue?.path?.length > 0) {
      return breadcrumbValue.path.join(' / ')
    }
    // Shape 4 — nested { value: string } (commerce regular facets)
    if (breadcrumbValue.value !== undefined && breadcrumbValue.value !== null) {
      return String(breadcrumbValue.value)
    }
    return ''
  }

  // Fallback safety net for any unexpected primitive
  return String(breadcrumbValue)
}

/**
 * Converts Coveo breadcrumb data into the Map<string, filter> shape that
 * SearchControlsBottom uses to render active-filter chips.
 */
export const mapBreadcrumbsToFilterMap = (facetBreadcrumbs = []) => {
  const map = new Map()
  facetBreadcrumbs.forEach((breadcrumb) => {
    breadcrumb.values.forEach(({ value: breadcrumbValue, deselect }) => {
      const label = resolveBreadcrumbLabel(breadcrumbValue)
      if (!label) return
      const key = `selectedFacet:${breadcrumb.field}:${label}`
      map.set(key, {
        group:     breadcrumb.field,
        label:     label,
        val:       label,
        type:      'selectedFacet',
        key:       breadcrumb.field,
        _deselect: deselect,
      })
    })
  })
  return map
}

/**
 * Converts Coveo automatic-facets to the [{key, label, type, buckets}]
 * shape that SearchFilter.js expects.
 */
export const mapCoveoFacets = (facets = []) => {
  return facets.map((facet) => ({
    key:    facet.state.field,
    label:  facet.state.displayName || facet.state.field,
    type:   'selectedFacet',
    buckets: (facet.state.values || []).map((v) => ({
      val:      v.value,
      count:    v.numberOfResults,
      selected: v.state === 'selected',
    })),
  }))
}

/**
 * Maps the Coveo sort-criteria expression back to the legacy customSort
 * string values so SearchControlsTop can keep its existing sort UI.
 */
export const getSortString = (sortState) => {
  const applied = sortState.appliedSort
  if (!applied || applied.by === 'relevance') return 'best_match'
  if (applied.by === 'fields') {
    const fields = applied.fields || []
    const priceField = fields.find((f) => f.name === 'ec_price')
    if (priceField) {
      return priceField.direction === 'desc' ? 'price_descending' : 'price_ascending'
    }
  }
  return 'best_match'
}

/**
 * Builds a Coveo fragment string from the current URL query params, keeping only
 * the params for which `paramFilter(key)` returns true.
 *
 * @param {(key: string) => boolean} [paramFilter] - Defaults to the commerce `isCoveoParam` predicate.
 */
export function getFragment(paramFilter = isCoveoParam, keyTransform = (key) => key) {
  if (typeof window === 'undefined') return ''
  const params = new URLSearchParams(window.location.search)
  const parts = []
  for (const [key, value] of params.entries()) {
    if (paramFilter(key)) {
      // URLSearchParams auto-decodes values. Pass them through as-is,
      // only encoding the three characters that break query-string structure.
      const safeValue = value
        .replace(/&/g, '%26') // e.g. brand "AT&T" → "f-brand=AT&T" would split into two params, fixed: "f-brand=AT%26T"
        .replace(/=/g, '%3D') // e.g. model "x=1" → "f-model=x=1" would split key/value incorrectly, fixed: "f-model=x%3D1"
        .replace(/#/g, '%23') // e.g. color "Red #5" → "f-color=Red #5" would start a URL hash, fixed: "f-color=Red %235"

      parts.push(`${keyTransform(key)}=${safeValue}`)
    }
  }
  return parts.join('&')
}

/**
 * Builds the content (classic headless) fragment string from the current URL —
 * i.e. only the automatic-facet (`af-`) params, used to seed/synchronize the
 * content engine's `UrlManager`.
 *
 * The address bar only ever shows content facets as `af-<field>` (regardless of whether
 * they're backed by a manually-built regular facet or a true automatic facet), so the
 * URL never has both `f-` and `af-` params for the same field. But classic headless only
 * restores a *regular* facet's selection from its native `f-<field>` key — an `af-<field>`
 * key is only recognised for automaticFacetSet. `regularFacetFields` lists the field names
 * of the manually-built (non-automatic) content facets, so their `af-<field>` URL params
 * get translated back to `f-<field>` here, letting classic headless restore the selection
 * directly into facetSet at construction/synchronize time — synchronously, before the
 * first search — instead of the param being silently dropped.
 *
 * @param {string[]} [regularFacetFields] - Field names of manually-built (non-automatic) content facets.
 */
export function getContentFragment(regularFacetFields = []) {
  const regularFields = new Set(regularFacetFields)
  return getFragment(isContentFacetParam, (key) => {
    if (!key.startsWith(AF_PREFIX)) return key
    const field = key.slice(AF_PREFIX.length)
    return regularFields.has(field) ? `f-${field}` : key
  })
}


/**
 * Returns true if Coveo analytics is enabled in the current privacy settings.
 * This is used to determine whether to send analytics events to Coveo.
 * @returns {boolean}
 */
export const isCoveoAnalyticsEnabled = () => {
  if (typeof window === 'undefined' || typeof window.getPrivacySettings !== 'function') {
    return false
  }

  return !!window.getPrivacySettings()?.performance
}

// ---------------------------------------------------------------------------
// Listing-page detection
// ---------------------------------------------------------------------------
/**
 * Query params that a bootstrap listing-page URL may carry without disqualifying it
 * from being treated as a listing page (as opposed to a full search-results page).
 */
const LISTING_BOOTSTRAP_QUERY_PARAMS = new Set(['tabType', LISTING_PAGE_MARKER, 'lang', 'locale', 'qsrc'])

/**
 * Determines whether the current page should be treated as a product-listing page
 * (as opposed to a full search-results page). All listing-page identification logic
 * lives here — see inline comments below for what each condition covers.
 *
 * @param {object} [params]
 * @param {boolean} [params.initialIsListingPage]
 * @param {string}  [params.insightLocale] - locale segment used to build the dedicated
 *   search-page path; if omitted, the search-page override is skipped.
 * @returns {boolean}
 */
export const resolveIsListingPage = ({ initialIsListingPage = false, insightLocale } = {}) => {
  if (typeof window === 'undefined') return initialIsListingPage

  const { pathname, search } = window.location

  // The dedicated search page is ALWAYS a search page, regardless of any other signal.
  const isSearchPage = pathname === `/${insightLocale}/search.html`
  if (isSearchPage) return false

  const params = new URLSearchParams(search)

  // Explicit `isListingPage=true` marker in the URL. Kept separate from the bootstrap-only
  // check below so a listing page can stay in listing mode even after filters/params are
  // applied that wouldn't otherwise pass the bootstrap allow-list.
  const hasListingMarker = params.get(LISTING_PAGE_MARKER) === 'true'
  // The decision was already cached earlier in the page lifecycle.
  const hasGlobalFlag = window[LISTING_PAGE_FLAG] === true

  // Server-rendered listing page, cached flag, or explicit marker — treat as listing.
  if (initialIsListingPage || hasGlobalFlag || hasListingMarker) return true

  // No query params at all — nothing suggests a search request yet.
  if (!params.size) return true

  // Only bootstrap-allowed params (tabType, isListingPage, lang, locale, qsrc) present —
  // still a listing page. Any other param means this is a real search request.
  for (const [key] of params.entries()) {
    if (!LISTING_BOOTSTRAP_QUERY_PARAMS.has(key)) return false
  }

  return true
}